import type { GroupProcessDto, ProcessDto, SiteDto } from '@app/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Cog,
  Layers,
  Pencil,
  Plus,
  PlusCircle,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchSites } from '../../admin-sites/api/index.js';
import { seedDemoProcessCartography } from '../../demo/api/index.js';
import { useDemoStore } from '../../demo/store/demo.store';
import {
  createGroupProcess,
  createProcess,
  createProcessStructureLink,
  deleteProcess,
  deleteStructureLink,
  fetchGroupProcesses,
  fetchProcesses,
  updateGroupProcess,
  updateProcess,
} from '../hooks/useProcessCartographyApi';

const PASTEL_COLORS = [
  '#f8f9fa',
  '#e3f2fd',
  '#f3e5f5',
  '#e8f5e8',
  '#fff3e0',
  '#fce4ec',
  '#e0f2f1',
  '#fff8e1',
  '#f1f8e9',
  '#e8eaf6',
  '#f3e8ff',
  '#fdf2e9',
];

const SITE_BADGE_CLASSES = [
  'bg-sky-600 text-white',
  'bg-emerald-600 text-white',
  'bg-violet-600 text-white',
  'bg-amber-600 text-white',
  'bg-rose-600 text-white',
  'bg-teal-600 text-white',
  'bg-indigo-600 text-white',
  'bg-orange-600 text-white',
];

function siteBadgeClass(siteId: string): string {
  let h = 0;
  for (let i = 0; i < siteId.length; i++) h = (h * 31 + siteId.charCodeAt(i)) | 0;
  return SITE_BADGE_CLASSES[Math.abs(h) % SITE_BADGE_CLASSES.length] ?? 'bg-slate-600 text-white';
}

const LINK_W = 180;
const LINK_H = 100;

function staggeredLayout(i: number) {
  return { positionX: 60 + i * 40, positionY: 60 + i * 40, width: LINK_W, height: LINK_H };
}

function groupSubtitle(processCount: number): string {
  return `Groupe de ${processCount} processus`;
}

type Banner = { kind: 'success' | 'error'; message: string } | null;

type GroupModalState = { mode: 'create' } | { mode: 'edit'; group: GroupProcessDto };

type ProcessModalState = {
  group: GroupProcessDto;
  editing: ProcessDto | null;
  name: string;
  description: string;
  sites: SiteDto[];
};

export function GroupsProcessesPage() {
  const queryClient = useQueryClient();
  const demoEnabled = useDemoStore((s) => s.enabled);
  const [banner, setBanner] = useState<Banner>(null);

  const [groupModal, setGroupModal] = useState<GroupModalState | null>(null);
  const [groupForm, setGroupForm] = useState({ name: '', color: PASTEL_COLORS[0] ?? '#f8f9fa' });

  const [processModal, setProcessModal] = useState<ProcessModalState | null>(null);

  const [draggedProcess, setDraggedProcess] = useState<ProcessDto | null>(null);
  const [dragOverGroupId, setDragOverGroupId] = useState<string | null>(null);

  const groupsQuery = useQuery({ queryKey: ['group-processes'], queryFn: fetchGroupProcesses });
  const processesQuery = useQuery({
    queryKey: ['processes'],
    queryFn: () => fetchProcesses(),
  });
  const sitesQuery = useQuery({ queryKey: ['sites-structure'], queryFn: fetchSites });

  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), 4000);
    return () => clearTimeout(t);
  }, [banner]);

  useEffect(() => {
    const clear = () => {
      setDraggedProcess(null);
      setDragOverGroupId(null);
    };
    document.addEventListener('dragend', clear);
    document.addEventListener('drop', clear);
    return () => {
      document.removeEventListener('dragend', clear);
      document.removeEventListener('drop', clear);
    };
  }, []);

  const groups = groupsQuery.data ?? [];
  const allProcesses = processesQuery.data ?? [];
  const sites = sitesQuery.data ?? [];

  const processCount = allProcesses.length;

  const processesByGroup = useMemo(() => {
    const m = new Map<string, ProcessDto[]>();
    for (const p of allProcesses) {
      const list = m.get(p.groupProcessId) ?? [];
      list.push(p);
      m.set(p.groupProcessId, list);
    }
    for (const list of m.values()) {
      list.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
    }
    return m;
  }, [allProcesses]);

  const invalidateAll = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['group-processes'] }),
      queryClient.invalidateQueries({ queryKey: ['processes'] }),
    ]);
  }, [queryClient]);

  const moveProcessMut = useMutation({
    mutationFn: ({ processId, groupProcessId }: { processId: string; groupProcessId: string }) =>
      updateProcess(processId, { groupProcessId }),
    onSuccess: async () => {
      await invalidateAll();
      setBanner({ kind: 'success', message: 'Processus déplacé vers le nouveau groupe.' });
    },
    onError: () => {
      setBanner({ kind: 'error', message: 'Impossible de déplacer le processus.' });
    },
  });

  const saveGroupMut = useMutation({
    mutationFn: async () => {
      if (!groupModal) return;
      const payload = { name: groupForm.name.trim(), color: groupForm.color };
      if (!payload.name) throw new Error('Nom requis');
      if (groupModal.mode === 'create') {
        await createGroupProcess(payload);
      } else {
        await updateGroupProcess(groupModal.group.id, payload);
      }
    },
    onSuccess: async () => {
      await invalidateAll();
      setGroupModal(null);
      setBanner({
        kind: 'success',
        message: groupModal?.mode === 'create' ? 'Groupe créé.' : 'Groupe modifié.',
      });
    },
    onError: () => {
      setBanner({ kind: 'error', message: 'Erreur lors de la sauvegarde du groupe.' });
    },
  });

  const openCreateGroup = () => {
    setGroupForm({ name: '', color: PASTEL_COLORS[0] ?? '#f8f9fa' });
    setGroupModal({ mode: 'create' });
  };

  const openEditGroup = (group: GroupProcessDto) => {
    setGroupForm({ name: group.name, color: group.color });
    setGroupModal({ mode: 'edit', group });
  };

  const openCreateProcess = (group: GroupProcessDto) => {
    setProcessModal({
      group,
      editing: null,
      name: '',
      description: '',
      sites: [],
    });
  };

  const openEditProcess = (group: GroupProcessDto, process: ProcessDto) => {
    const linkedAsSites: SiteDto[] = process.linkedSites.map((ls) => {
      const full = sites.find((s) => s.id === ls.id);
      if (full) return full;
      const t0 = new Date(0).toISOString();
      return {
        id: ls.id,
        name: ls.name,
        companyId: process.companyId,
        addressLine1: null,
        addressLine2: null,
        postalCode: null,
        city: null,
        country: null,
        createdAt: t0,
        updatedAt: t0,
      };
    });
    setProcessModal({
      group,
      editing: process,
      name: process.name,
      description: process.description ?? '',
      sites: linkedAsSites,
    });
  };

  const saveProcessMut = useMutation({
    mutationFn: async () => {
      if (!processModal) return;
      const name = processModal.name.trim();
      if (!name) throw new Error('Nom requis');
      if (processModal.sites.length === 0) throw new Error('Au moins un site');

      if (processModal.editing) {
        const pid = processModal.editing.id;
        await updateProcess(pid, {
          name,
          description: processModal.description.trim() || null,
        });

        const initial = new Map(processModal.editing.linkedSites.map((ls) => [ls.id, ls]));
        const nextIds = new Set(processModal.sites.map((s) => s.id));

        const toRemove = processModal.editing.linkedSites.filter((ls) => !nextIds.has(ls.id));
        const toAdd = processModal.sites.filter((s) => !initial.has(s.id));

        await Promise.all(toRemove.map((ls) => deleteStructureLink(ls.id, ls.structureLinkId)));

        let i = 0;
        await Promise.all(
          toAdd.map((site) => {
            const lay = staggeredLayout(i++);
            return createProcessStructureLink(site.id, {
              processId: pid,
              positionX: lay.positionX,
              positionY: lay.positionY,
              width: lay.width,
              height: lay.height,
            });
          }),
        );
      } else {
        const created = await createProcess({
          groupProcessId: processModal.group.id,
          name,
          description: processModal.description.trim() || undefined,
          active: true,
        });
        await Promise.all(
          processModal.sites.map((site, i) => {
            const lay = staggeredLayout(i);
            return createProcessStructureLink(site.id, {
              processId: created.id,
              positionX: lay.positionX,
              positionY: lay.positionY,
              width: lay.width,
              height: lay.height,
            });
          }),
        );
      }
    },
    onSuccess: async () => {
      await invalidateAll();
      setProcessModal(null);
      setBanner({
        kind: 'success',
        message: processModal?.editing ? 'Processus mis à jour.' : 'Processus créé.',
      });
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : '';
      if (msg === 'Au moins un site') {
        setBanner({ kind: 'error', message: 'Veuillez sélectionner au moins un site.' });
      } else {
        setBanner({ kind: 'error', message: 'Erreur lors de la sauvegarde du processus.' });
      }
    },
  });

  const deleteProcessMut = useMutation({
    mutationFn: deleteProcess,
    onSuccess: async () => {
      await invalidateAll();
      setBanner({ kind: 'success', message: 'Processus supprimé.' });
    },
    onError: () => {
      setBanner({
        kind: 'error',
        message: 'Impossible de supprimer ce processus (fiches items associées ou erreur serveur).',
      });
    },
  });

  const seedDemoMut = useMutation({
    mutationFn: seedDemoProcessCartography,
    onSuccess: async (res) => {
      await invalidateAll();
      setBanner({ kind: 'success', message: res.message });
    },
    onError: (err: unknown) => {
      const msg =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'message' in err.response.data
          ? String((err.response.data as { message?: unknown }).message)
          : err instanceof Error
            ? err.message
            : 'Échec du remplissage démo.';
      setBanner({ kind: 'error', message: msg });
    },
  });

  const handleDropOnGroup = (e: React.DragEvent, targetGroup: GroupProcessDto) => {
    e.preventDefault();
    if (!draggedProcess || draggedProcess.groupProcessId === targetGroup.id) {
      setDragOverGroupId(null);
      setDraggedProcess(null);
      return;
    }
    moveProcessMut.mutate({
      processId: draggedProcess.id,
      groupProcessId: targetGroup.id,
    });
    setDragOverGroupId(null);
    setDraggedProcess(null);
  };

  const loading = groupsQuery.isLoading || processesQuery.isLoading || sitesQuery.isLoading;
  const error = groupsQuery.error ?? processesQuery.error;

  if (loading) {
    return (
      <div className="flex min-h-[300px] flex-col items-center justify-center gap-3 p-8">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-emerald-500" />
        <p className="text-slate-500 text-sm">Chargement des structures…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-lg rounded-xl border border-red-200 bg-red-50 p-6 text-red-800">
        <p className="font-semibold">Erreur de chargement</p>
        <p className="mt-2 text-sm">Impossible de charger les groupes et processus.</p>
      </div>
    );
  }

  return (
    <div className="structure-manager w-full pb-16">
      {banner ? (
        <div
          className={`mb-4 rounded-xl px-4 py-3 text-sm ${
            banner.kind === 'success'
              ? 'border border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border border-red-200 bg-red-50 text-red-900'
          }`}
        >
          {banner.message}
        </div>
      ) : null}

      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-bold text-2xl text-slate-900 tracking-tight">Groupes et Processus</h1>
          <p className="mt-1 text-slate-600">
            Gestion des groupes de processus et des processus métiers
          </p>
        </div>
        {demoEnabled ? (
          <button
            type="button"
            disabled={seedDemoMut.isPending}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border-2 border-amber-400 bg-amber-50 px-4 py-2.5 font-semibold text-amber-950 text-sm shadow-sm transition-colors hover:bg-amber-100 disabled:opacity-60"
            onClick={() => {
              if (
                !confirm(
                  'Remplir automatiquement la démo : 3 groupes (LEADERSHIP, MANAGEMENT OPÉRATIONNEL, MANAGEMENT SUPPORT), 5 processus avec descriptions et rattachement aux sites Paris / Lyon sur la cartographie ?',
                )
              ) {
                return;
              }
              seedDemoMut.mutate();
            }}
          >
            <Sparkles className="h-4 w-4" aria-hidden />
            {seedDemoMut.isPending ? 'Chargement…' : 'Remplir la démo'}
          </button>
        ) : null}
      </header>

      {/* Statistiques */}
      <div className="mb-8 grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border-2 border-blue-200 bg-white p-5 text-center shadow-sm">
          <Layers className="mx-auto mb-2 h-8 w-8 text-blue-600" aria-hidden />
          <p className="font-bold text-3xl text-blue-700">{groups.length}</p>
          <p className="text-slate-500 text-sm">Groupes</p>
        </div>
        <div className="rounded-2xl border-2 border-emerald-200 bg-white p-5 text-center shadow-sm">
          <Cog className="mx-auto mb-2 h-8 w-8 text-emerald-600" aria-hidden />
          <p className="font-bold text-3xl text-emerald-700">{processCount}</p>
          <p className="text-slate-500 text-sm">Processus</p>
        </div>
        <div className="rounded-2xl border-2 border-sky-200 bg-white p-5 text-center shadow-sm">
          <Building2 className="mx-auto mb-2 h-8 w-8 text-sky-600" aria-hidden />
          <p className="font-bold text-3xl text-sky-700">{sites.length}</p>
          <p className="text-slate-500 text-sm">Site{sites.length > 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {groups.map((group) => {
          const procs = processesByGroup.get(group.id) ?? [];
          const n = procs.length;
          const isOver = dragOverGroupId === group.id;

          return (
            <div key={group.id} className="flex h-full">
              <div
                className={`flex h-full min-h-[320px] w-full flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition-all ${
                  isOver
                    ? 'scale-[1.02] border-2 border-dashed border-emerald-500 bg-emerald-50/50'
                    : 'border-slate-200'
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDragOverGroupId(group.id);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setDragOverGroupId(null);
                  }
                }}
                onDrop={(e) => handleDropOnGroup(e, group)}
              >
                <div
                  className="flex items-start justify-between gap-2 px-4 py-3"
                  style={{ backgroundColor: group.color }}
                >
                  <div className="min-w-0">
                    <h2 className="flex items-center gap-2 font-bold text-slate-900 text-sm uppercase tracking-wide">
                      <Layers className="h-4 w-4 shrink-0 text-slate-700" aria-hidden />
                      <span className="truncate">{group.name}</span>
                      {isOver ? (
                        <span className="shrink-0 rounded-full bg-emerald-600 px-2 py-0.5 text-[0.65rem] text-white">
                          Déposer ici
                        </span>
                      ) : null}
                    </h2>
                    <p className="mt-0.5 text-slate-600 text-xs">{groupSubtitle(n)}</p>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded-lg p-1.5 text-slate-600 transition-colors hover:bg-slate-900/10 hover:text-slate-900"
                    title={`Modifier le groupe ${group.name}`}
                    onClick={() => openEditGroup(group)}
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                  </button>
                </div>

                <div className="flex flex-1 flex-col px-3 py-2">
                  {procs.length === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center py-8 text-center text-slate-400">
                      <p className="text-sm">Aucun processus dans ce groupe</p>
                    </div>
                  ) : (
                    <div className="flex flex-col divide-y divide-slate-100">
                      {procs.map((process) => (
                        <div
                          key={process.id}
                          draggable
                          onDragStart={(e) => {
                            setDraggedProcess(process);
                            e.dataTransfer.effectAllowed = 'move';
                            (e.target as HTMLElement).style.opacity = '0.5';
                          }}
                          onDragEnd={(e) => {
                            (e.target as HTMLElement).style.opacity = '1';
                          }}
                          className="group/p py-3 transition-colors hover:bg-slate-50"
                          title="Glisser-déposer pour déplacer vers un autre groupe"
                        >
                          <div className="flex justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <h3 className="flex items-center gap-1.5 font-semibold text-slate-900 text-sm">
                                <Cog
                                  className="h-3.5 w-3.5 shrink-0 text-emerald-600"
                                  aria-hidden
                                />
                                <span className="truncate">{process.name}</span>
                                {draggedProcess?.id === process.id ? (
                                  <span className="shrink-0 rounded bg-amber-200 px-1.5 py-0.5 text-[0.6rem] text-amber-950">
                                    Déplacement…
                                  </span>
                                ) : null}
                                {!process.active ? (
                                  <span className="shrink-0 rounded bg-slate-200 px-1.5 py-0.5 text-[0.6rem] text-slate-700">
                                    Inactif
                                  </span>
                                ) : null}
                              </h3>
                              <p className="mt-0.5 text-slate-500 text-xs">
                                {process.description?.trim()
                                  ? process.description
                                  : 'Aucune description'}
                              </p>
                              {process.linkedSites.length > 0 ? (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {process.linkedSites.map((site) => (
                                    <span
                                      key={site.id}
                                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium text-[0.7rem] ${siteBadgeClass(site.id)}`}
                                      title={site.name}
                                    >
                                      <Building2 className="h-3 w-3" aria-hidden />
                                      {site.name}
                                    </span>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                            <div className="flex shrink-0 gap-0.5">
                              <button
                                type="button"
                                className="rounded-lg p-1.5 text-sky-600 hover:bg-sky-50"
                                title="Modifier"
                                onClick={() => openEditProcess(group, process)}
                              >
                                <Pencil className="h-4 w-4" aria-hidden />
                              </button>
                              {process.itemsCount === 0 ? (
                                <button
                                  type="button"
                                  className="rounded-lg p-1.5 text-red-600 hover:bg-red-50"
                                  title="Supprimer"
                                  onClick={() => {
                                    if (
                                      confirm(
                                        `Êtes-vous sûr de vouloir supprimer le processus « ${process.name} » ?`,
                                      )
                                    ) {
                                      deleteProcessMut.mutate(process.id);
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" aria-hidden />
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-auto border-slate-100 border-t p-3">
                  <button
                    type="button"
                    className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-blue-200 bg-white py-2.5 font-medium text-blue-700 text-sm transition-colors hover:bg-blue-50"
                    onClick={() => openCreateProcess(group)}
                  >
                    <Plus className="h-4 w-4" aria-hidden />
                    Ajouter un processus
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {/* Tuile nouveau groupe */}
        <button
          type="button"
          className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-emerald-500 bg-white p-6 text-center shadow-sm transition-all hover:border-emerald-700 hover:bg-emerald-50/40"
          onClick={openCreateGroup}
        >
          <PlusCircle className="mb-3 h-16 w-16 text-emerald-600 opacity-80" aria-hidden />
          <h3 className="font-semibold text-emerald-800 text-lg">Ajouter un nouveau groupe</h3>
          <p className="mt-2 max-w-xs text-slate-500 text-sm">
            Créez un nouveau groupe de processus pour organiser vos activités
          </p>
          <span className="mt-4 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 font-semibold text-white">
            <Plus className="h-5 w-5" aria-hidden />
            Nouveau Groupe
          </span>
        </button>
      </div>

      {groups.length === 0 ? (
        <div className="mt-12 text-center text-slate-500">
          <p className="font-medium text-slate-700 text-lg">Aucune structure définie</p>
          <p className="mt-2 text-sm">Commencez par créer un groupe de processus.</p>
          <button
            type="button"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700"
            onClick={openCreateGroup}
          >
            <Plus className="h-4 w-4" aria-hidden />
            Créer le premier groupe
          </button>
        </div>
      ) : null}

      {/* Modale groupe */}
      {groupModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-slate-100 border-b px-5 py-4">
              <h3 className="flex items-center gap-2 font-semibold text-slate-900 text-lg">
                {groupModal.mode === 'create' ? (
                  <Plus className="h-5 w-5 text-emerald-600" aria-hidden />
                ) : (
                  <Pencil className="h-5 w-5 text-blue-600" aria-hidden />
                )}
                {groupModal.mode === 'create' ? 'Créer un nouveau groupe' : 'Modifier le groupe'}
              </h3>
              <button
                type="button"
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                onClick={() => setGroupModal(null)}
                aria-label="Fermer"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              <div>
                <label className="mb-1 block font-medium text-slate-700 text-sm" htmlFor="grp-name">
                  Nom du groupe
                </label>
                <input
                  id="grp-name"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={groupForm.name}
                  onChange={(e) => setGroupForm((s) => ({ ...s, name: e.target.value }))}
                  placeholder="Nom du groupe"
                />
              </div>
              <div>
                <p className="mb-2 font-medium text-slate-700 text-sm">Couleur du groupe</p>
                <div className="grid grid-cols-6 gap-2 sm:grid-cols-8">
                  {PASTEL_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      title={color}
                      className={`h-10 rounded-lg border-2 transition-transform hover:scale-105 ${
                        groupForm.color === color
                          ? 'border-slate-800 ring-2 ring-slate-300'
                          : 'border-slate-200'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setGroupForm((s) => ({ ...s, color }))}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-slate-100 border-t px-5 py-4">
              <button
                type="button"
                className="rounded-xl border border-slate-200 px-4 py-2 text-slate-700 text-sm hover:bg-slate-50"
                onClick={() => setGroupModal(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={saveGroupMut.isPending || !groupForm.name.trim()}
                className="rounded-xl bg-blue-600 px-4 py-2 font-medium text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={() => saveGroupMut.mutate()}
              >
                {groupModal.mode === 'create' ? 'Créer' : 'Sauvegarder'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Modale processus */}
      {processModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-slate-100 border-b px-5 py-4">
              <h3 className="font-semibold text-slate-900">
                {processModal.editing
                  ? `Modifier le processus « ${processModal.editing.name} »`
                  : `Ajouter un processus au groupe « ${processModal.group.name} »`}
              </h3>
              <button
                type="button"
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100"
                onClick={() => setProcessModal(null)}
                aria-label="Fermer"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              <div>
                <label
                  className="mb-1 block font-medium text-slate-700 text-sm"
                  htmlFor="proc-name"
                >
                  Nom du processus <span className="text-red-500">*</span>
                </label>
                <input
                  id="proc-name"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={processModal.name}
                  onChange={(e) => setProcessModal((s) => (s ? { ...s, name: e.target.value } : s))}
                />
              </div>
              <div>
                <label
                  className="mb-1 block font-medium text-slate-700 text-sm"
                  htmlFor="proc-desc"
                >
                  Description
                </label>
                <textarea
                  id="proc-desc"
                  rows={3}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value={processModal.description}
                  onChange={(e) =>
                    setProcessModal((s) => (s ? { ...s, description: e.target.value } : s))
                  }
                  placeholder="Description (optionnel)"
                />
              </div>
              <div>
                <p className="mb-1 font-medium text-slate-700 text-sm">Sites concernés</p>
                <select
                  className="mb-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  value=""
                  onChange={(e) => {
                    const id = e.target.value;
                    if (!id || !processModal) return;
                    const site = sites.find((s) => s.id === id);
                    if (site && !processModal.sites.some((s) => s.id === id)) {
                      setProcessModal((s) => (s ? { ...s, sites: [...s.sites, site] } : s));
                    }
                    e.target.value = '';
                  }}
                >
                  <option value="">Sélectionner un site à ajouter…</option>
                  {sites
                    .filter((site) => !processModal.sites.some((s) => s.id === site.id))
                    .map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}
                        {site.city && site.country
                          ? ` (${site.city}, ${site.country})`
                          : site.city
                            ? ` (${site.city})`
                            : ''}
                      </option>
                    ))}
                </select>
                {processModal.sites.length > 0 ? (
                  <div className="min-h-[60px] rounded-xl border border-slate-200 p-3">
                    <p className="mb-2 text-slate-500 text-xs">
                      Sites sélectionnés ({processModal.sites.length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {processModal.sites.map((site) => (
                        <button
                          key={site.id}
                          type="button"
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-medium text-xs ${siteBadgeClass(site.id)}`}
                          onClick={() =>
                            setProcessModal((s) =>
                              s ? { ...s, sites: s.sites.filter((x) => x.id !== site.id) } : s,
                            )
                          }
                          title="Cliquer pour retirer"
                        >
                          <Building2 className="h-3 w-3" aria-hidden />
                          {site.name}
                          <X className="h-3 w-3 opacity-80" aria-hidden />
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex min-h-[60px] items-center justify-center rounded-xl border border-dashed border-slate-200 text-slate-400 text-sm">
                    Aucun site sélectionné
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 border-slate-100 border-t px-5 py-4">
              <button
                type="button"
                className="rounded-xl border border-slate-200 px-4 py-2 text-slate-700 text-sm hover:bg-slate-50"
                onClick={() => setProcessModal(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={
                  saveProcessMut.isPending ||
                  !processModal.name.trim() ||
                  processModal.sites.length === 0
                }
                className="rounded-xl bg-blue-600 px-4 py-2 font-medium text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={() => saveProcessMut.mutate()}
              >
                {processModal.editing ? 'Sauvegarder' : 'Créer le processus'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
